/**
 * The sample movement area for OEMA and a sample urgent-inspection record.
 *
 * The movement area is the one the sample drawing carries (`geo.ts`): two
 * runways at their published designators, taxiways A to E, and three aprons.
 * On a deployment it would be read from the platform's zones; the shape here is
 * the shape that read would produce.
 *
 * The record holds one closed inspection — a sandstorm a fortnight ago, closed
 * with the remote apron restricted — and one OPEN, after this morning's heavy
 * rain, half done: RWY 17/35 fully assessed with standing water in its last
 * third, taxiway B with debris found and cleared, the remote apron with
 * equipment moved and not yet secured, and everything else still to walk. It
 * is open so the screen shows an inspection in progress; start a new one by
 * closing it.
 *
 * Every time is placed relative to `TODAY` from `./clock`, as every sample
 * record is.
 */
import type { AreaCheck, MovementArea, UrgentRegister } from "../domain/urgentInspection";
import { TODAY } from "./clock";

export const OEMA_MOVEMENT_AREA: MovementArea = {
  runways: [
    { zone: "RWY-17-35", designator: "17/35", from: "17", lengthM: 4335 },
    { zone: "RWY-18-36", designator: "18/36", from: "18", lengthM: 3050 },
  ],
  taxiways: [
    { zone: "TWY-A", name: "Taxiway A" },
    { zone: "TWY-B", name: "Taxiway B" },
    { zone: "TWY-C", name: "Taxiway C" },
    { zone: "TWY-D", name: "Taxiway D" },
    { zone: "TWY-E", name: "Taxiway E" },
  ],
  aprons: [
    { zone: "APRON-1", name: "Apron 1 (contact stands)" },
    { zone: "APRON-2", name: "Apron 2" },
    { zone: "APRON-R", name: "Remote apron" },
  ],
};

/** A time `hours` from the sample clock, ISO. */
function at(hours: number): string {
  return new Date(TODAY.getTime() + hours * 3_600_000).toISOString();
}

const OK_RWY = {
  sand: "ok",
  fod: "ok",
  damage: "ok",
  lights: "ok",
  markings: "ok",
  drainage: "ok",
  wildlife: "ok",
} as const;
const OK_TWY = {
  water: "ok",
  snowIce: "ok",
  sand: "ok",
  fod: "ok",
  damage: "ok",
  lights: "ok",
  markings: "ok",
  drainage: "ok",
} as const;
const OK_APR = {
  water: "ok",
  fod: "ok",
  damage: "ok",
  lights: "ok",
  drainage: "ok",
  equipment: "ok",
  stands: "ok",
  fences: "ok",
} as const;

export function mockUrgent(): UrgentRegister {
  const INSPECTOR = "Airside Operations duty officer";
  const past: AreaCheck[] = [
    ...(["RWY-17-35", "RWY-18-36"] as const).flatMap((rw) =>
      ([1, 2, 3] as const).map((n): AreaCheck => ({
        id: `p-${rw}-${n}`,
        areaId: `${rw}:${n}`,
        at: at(-14 * 24 + 1 + n / 10),
        by: INSPECTOR,
        checks: { ...OK_RWY, sand: n === 2 ? "cleared" : "ok" },
        notes: n === 2 ? { sand: "sand drift across the centre line, swept" } : undefined,
        contaminant: "dry",
      })),
    ),
    ...["TWY-A", "TWY-B", "TWY-C", "TWY-D", "TWY-E"].map((z, k): AreaCheck => ({
      id: `p-${z}`,
      areaId: z,
      at: at(-14 * 24 + 2 + k / 10),
      by: INSPECTOR,
      checks: { ...OK_TWY },
    })),
    { id: "p-APRON-1", areaId: "APRON-1", at: at(-14 * 24 + 2.6), by: INSPECTOR, checks: { ...OK_APR } },
    {
      id: "p-APRON-2",
      areaId: "APRON-2",
      at: at(-14 * 24 + 2.7),
      by: INSPECTOR,
      checks: { ...OK_APR, fod: "cleared" },
      notes: { fod: "plastic sheeting and cardboard blown onto stands 21–23, collected" },
    },
    {
      id: "p-APRON-R",
      areaId: "APRON-R",
      at: at(-14 * 24 + 2.8),
      by: INSPECTOR,
      checks: { ...OK_APR, fences: "found" },
      notes: { fences: "blast screen panel at R4 torn from its frame" },
    },
  ];

  const now: AreaCheck[] = [
    {
      id: "n-17-1",
      areaId: "RWY-17-35:1",
      at: at(-3.2),
      by: INSPECTOR,
      checks: { ...OK_RWY },
      contaminant: "wet",
      coveragePct: 100,
    },
    {
      id: "n-17-2",
      areaId: "RWY-17-35:2",
      at: at(-3.1),
      by: INSPECTOR,
      checks: { ...OK_RWY, wildlife: "cleared" },
      notes: { wildlife: "flock of cattle egrets at the mid-point, dispersed" },
      contaminant: "wet",
      coveragePct: 100,
    },
    {
      id: "n-17-3",
      areaId: "RWY-17-35:3",
      at: at(-3.0),
      by: INSPECTOR,
      checks: { ...OK_RWY, drainage: "found" },
      notes: { drainage: "edge drain at the 35 end overflowing; ponding across the south half" },
      contaminant: "standingWater",
      depthMm: 6,
      coveragePct: 60,
    },
    {
      id: "n-TWY-B",
      areaId: "TWY-B",
      at: at(-2.6),
      by: INSPECTOR,
      checks: { ...OK_TWY, fod: "cleared" },
      notes: { fod: "washed-in gravel at B1 holding position, swept" },
    },
    {
      id: "n-APRON-R",
      areaId: "APRON-R",
      at: at(-2.2),
      by: INSPECTOR,
      checks: { ...OK_APR, equipment: "found", water: "found" },
      notes: {
        equipment: "two baggage dollies and a set of steps rolled from R2 into the taxilane",
        water: "ponding on stands R3–R4",
      },
    },
  ];

  return {
    inspections: [
      {
        id: "ui-past",
        trigger: "sandstorm",
        triggerNote: "Dust storm, visibility below 800 m for two hours",
        openedAt: at(-14 * 24 + 0.5),
        openedBy: INSPECTOR,
        checks: past,
        log: [
          {
            at: at(-14 * 24 + 0.5),
            by: INSPECTOR,
            what: "Urgent inspection opened after: {trigger}",
            vars: { trigger: "sandstorm" },
          },
          {
            at: at(-14 * 24 + 3),
            by: INSPECTOR,
            what: "Closed with restrictions: {areas}",
            vars: { areas: "APRON-R" },
          },
        ],
        closed: {
          at: at(-14 * 24 + 3),
          by: INSPECTOR,
          outcome: "restricted",
          note: "Stand R4 withdrawn until the blast screen is repaired",
          restricted: ["APRON-R"],
        },
      },
      {
        id: "ui-now",
        trigger: "rain",
        triggerNote: "Thunderstorm with heavy rain, 04:00–05:30",
        openedAt: at(-3.5),
        openedBy: INSPECTOR,
        checks: now,
        log: [
          { at: at(-3.5), by: INSPECTOR, what: "Urgent inspection opened after: {trigger}", vars: { trigger: "rain" } },
          ...now.map((c) => ({
            at: c.at,
            by: c.by,
            what: "{area} inspected: {verdict}",
            vars: {
              area: c.areaId,
              verdict: Object.values(c.checks).includes("found")
                ? "unsafe"
                : Object.values(c.checks).includes("cleared") ||
                    c.contaminant === "wet" ||
                    c.contaminant === "standingWater"
                  ? "caution"
                  : "clear",
            },
          })),
        ],
      },
    ],
  };
}
